import './Read.scss';
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { generateHTML } from '@tiptap/html';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Navbar from "../../components/Navbar/Navbar";
import AuthContext from "../../context/AuthContext";
import SnackbarContext from "../../context/SnackbarContext";
import { API_BASE_URL } from '../../config';
import { authHeaders } from '../../utils/api';
import { formatDate, sameDay } from '../../utils/dates';

const EXTENSIONS = [StarterKit, Underline];

function absoluteUrl(path) {
    if (!path) return '';
    if (/^https?:/i.test(path)) return path;
    return `${API_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
}

function renderContent(raw) {
    if (!raw) return '';
    let doc;
    if (typeof raw === 'object') doc = raw;
    else {
        try { doc = JSON.parse(raw); }
        catch { return raw.replace(/\n/g, '<br>'); }
    }
    try { return generateHTML(doc, EXTENSIONS); } catch { return ''; }
}

function PanelIcon({ collapsed }) {
    // A sidebar panel with a chevron inside. The chevron points outward when
    // expanded (= "collapse me") and inward when collapsed (= "expand me").
    // The visual is direction-stable; works identically in LTR and RTL.
    const chevron = collapsed ? '8 8 12 12 8 16' : '12 8 8 12 12 16';
    return (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
             stroke="currentColor" strokeWidth="1.7"
             strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <line x1="15" y1="4" x2="15" y2="20" />
            <polyline points={chevron} />
        </svg>
    );
}

function Stars({ value, onSelect, interactive, size = 22 }) {
    const [hover, setHover] = useState(null);
    const display = hover ?? value ?? 0;
    return (
        <div className="stars" role={interactive ? 'radiogroup' : undefined}>
            {[1, 2, 3, 4, 5].map(n => {
                const active = display >= n;
                if (!interactive) {
                    return (
                        <svg key={n} viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
                            <path
                                fill={active ? '#DAA520' : '#ddd'}
                                d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14 2 9.27l6.91-1.01L12 2z"
                            />
                        </svg>
                    );
                }
                return (
                    <button
                        key={n}
                        type="button"
                        className="stars-btn"
                        onMouseEnter={() => setHover(n)}
                        onMouseLeave={() => setHover(null)}
                        onClick={() => onSelect?.(n)}
                        aria-label={`${n} نجوم`}
                    >
                        <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
                            <path
                                fill={active ? '#DAA520' : '#ddd'}
                                d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14 2 9.27l6.91-1.01L12 2z"
                            />
                        </svg>
                    </button>
                );
            })}
        </div>
    );
}

export default function Read() {

    const { id } = useParams();
    const navigate = useNavigate();
    const { user, authTokens } = useContext(AuthContext);
    const { setSnackbar } = useContext(SnackbarContext);

    const [book, setBook] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeIdx, setActiveIdx] = useState(0);
    const [chaptersHidden, setChaptersHidden] = useState(false);
    const [bookmarking, setBookmarking] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await fetch(`${API_BASE_URL}/api/books/library/book/?id=${encodeURIComponent(id)}`, {
                    headers: authHeaders(authTokens),
                });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const data = await r.json();
                if (!cancelled) setBook(data.book);
            } catch (err) {
                if (!cancelled) {
                    setError('تعذر تحميل الكتاب');
                    setSnackbar({ content: 'تعذر تحميل الكتاب', success: false });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [id, authTokens, setSnackbar]);

    const chapters = book?.chapters ?? [];
    const activeChapter = chapters[activeIdx];
    const activeHtml = useMemo(
        () => activeChapter ? renderContent(activeChapter.content) : '',
        [activeChapter]
    );

    const rate = async (value) => {
        if (!user) {
            setSnackbar({ content: 'سجّل الدخول لتقييم الكتاب', success: false });
            return;
        }
        try {
            const r = await fetch(`${API_BASE_URL}/api/books/rate/`, {
                method: 'POST',
                headers: { ...authHeaders(authTokens), 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: book.id, value }),
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const updated = await r.json();
            setBook(updated);
            setSnackbar({ content: 'شكرًا على تقييمك', success: true });
        } catch {
            setSnackbar({ content: 'تعذر إرسال التقييم', success: false });
        }
    };

    const removeRating = async () => {
        try {
            const r = await fetch(`${API_BASE_URL}/api/books/rate/?id=${book.id}`, {
                method: 'DELETE',
                headers: authHeaders(authTokens),
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const updated = await r.json();
            setBook(updated);
            setSnackbar({ content: 'تم حذف تقييمك', success: true });
        } catch {
            setSnackbar({ content: 'تعذر حذف التقييم', success: false });
        }
    };

    const toggleBookmark = async () => {
        if (!user) {
            setSnackbar({ content: 'سجّل الدخول لحفظ العلامات المرجعية', success: false });
            return;
        }
        setBookmarking(true);
        try {
            if (book.is_bookmarked) {
                await fetch(`${API_BASE_URL}/api/books/bookmarks/?book=${book.id}`, {
                    method: 'DELETE',
                    headers: authHeaders(authTokens),
                });
                setBook(b => ({ ...b, is_bookmarked: false }));
                setSnackbar({ content: 'تم حذف العلامة', success: true });
            } else {
                await fetch(`${API_BASE_URL}/api/books/bookmarks/`, {
                    method: 'POST',
                    headers: { ...authHeaders(authTokens), 'Content-Type': 'application/json' },
                    body: JSON.stringify({ book: book.id }),
                });
                setBook(b => ({ ...b, is_bookmarked: true }));
                setSnackbar({ content: 'تم حفظ العلامة', success: true });
            }
        } catch {
            setSnackbar({ content: 'تعذر تحديث العلامة', success: false });
        } finally {
            setBookmarking(false);
        }
    };

    const bookmarkChapter = async () => {
        if (!user) {
            setSnackbar({ content: 'سجّل الدخول لحفظ العلامات المرجعية', success: false });
            return;
        }
        if (!activeChapter) return;
        setBookmarking(true);
        try {
            await fetch(`${API_BASE_URL}/api/books/bookmarks/`, {
                method: 'POST',
                headers: { ...authHeaders(authTokens), 'Content-Type': 'application/json' },
                body: JSON.stringify({ book: book.id, chapter: activeChapter.id }),
            });
            setSnackbar({ content: `تم حفظ علامة عند: ${activeChapter.title || `الفصل ${activeIdx + 1}`}`, success: true });
        } catch {
            setSnackbar({ content: 'تعذر حفظ العلامة', success: false });
        } finally {
            setBookmarking(false);
        }
    };

    return (
        <>
        <Navbar />
        <main className={'read-page' + (chapters.length > 1 && chaptersHidden ? ' is-focus' : '')}>
            <div className='read-container'>
                <div className='read-toolbar'>
                    <button className='read-back-btn' onClick={() => navigate('/library')}>
                        العودة للمكتبة
                    </button>
                    <div className='read-toolbar-actions'>
                        {book && (
                            <button
                                type='button'
                                className={'read-toolbar-bookmark' + (book.is_bookmarked ? ' is-active' : '')}
                                onClick={toggleBookmark}
                                disabled={bookmarking}
                                aria-pressed={book.is_bookmarked}
                                title={book.is_bookmarked ? 'إزالة من العلامات' : 'إضافة إلى العلامات'}
                            >
                                {book.is_bookmarked ? '★ محفوظ' : '☆ احفظ'}
                            </button>
                        )}
                    </div>
                </div>

                {loading ? (
                    <p className='read-empty'>جارٍ التحميل…</p>
                ) : error || !book ? (
                    <p className='read-empty'>{error ?? 'الكتاب غير موجود'}</p>
                ) : (
                    <div className={
                        'read-layout' +
                        (chapters.length > 1
                            ? (chaptersHidden ? ' has-toc has-toc--collapsed' : ' has-toc')
                            : '')
                    }>
                        {chapters.length > 1 && (
                            chaptersHidden ? (
                                <aside className='read-toc is-collapsed'>
                                    <button
                                        type='button'
                                        className='read-toc-toggle'
                                        onClick={() => setChaptersHidden(false)}
                                        aria-label='إظهار قائمة الفصول'
                                        title='إظهار الفصول'
                                    >
                                        <PanelIcon collapsed />
                                    </button>
                                </aside>
                            ) : (
                                <aside className='read-toc'>
                                    <div className='read-toc-header'>
                                        <span>الفصول</span>
                                        <button
                                            type='button'
                                            className='read-toc-toggle'
                                            onClick={() => setChaptersHidden(true)}
                                            aria-label='إخفاء قائمة الفصول'
                                            title='إخفاء'
                                        >
                                            <PanelIcon />
                                        </button>
                                    </div>
                                    <ol className='read-toc-list'>
                                        {chapters.map((c, i) => (
                                            <li key={c.id} className={i === activeIdx ? 'is-active' : ''}>
                                                <button onClick={() => setActiveIdx(i)}>
                                                    <span className='read-toc-num'>{i + 1}</span>
                                                    <span>{c.title || `الفصل ${i + 1}`}</span>
                                                </button>
                                            </li>
                                        ))}
                                    </ol>
                                </aside>
                            )
                        )}

                        <article className='read-article'>
                            <header className='read-header'>
                                {book.cover_image && (
                                    <img
                                        className='read-cover'
                                        src={absoluteUrl(book.cover_image)}
                                        alt={`غلاف ${book.title}`}
                                    />
                                )}
                                <div className='read-header-text'>
                                    <h1 className='read-title'>{book.title}</h1>
                                    <p className='read-author'>بقلم {book.display_author || book.author}</p>
                                    {book.category_label && (
                                        <span className='read-category-tag'>{book.category_label}</span>
                                    )}
                                    {book.published_at && (
                                        <p className='read-dates'>
                                            نُشر في {formatDate(book.published_at)}
                                            {!sameDay(book.published_at, book.date_modified) && (
                                                <> · آخر تحديث {formatDate(book.date_modified)}</>
                                            )}
                                        </p>
                                    )}

                                    <div className='read-stats'>
                                        <span className='read-views'>
                                            {(book.view_count ?? 0).toLocaleString('ar-EG')} مشاهدة
                                        </span>
                                        <span className='read-rating-summary'>
                                            <Stars value={Math.round(book.avg_rating ?? 0)} size={18} />
                                            {book.avg_rating
                                                ? <span>{book.avg_rating} ({book.rating_count})</span>
                                                : <span>لا تقييمات بعد</span>}
                                        </span>
                                    </div>

                                    {book.description && (
                                        <p className='read-description'>{book.description}</p>
                                    )}
                                </div>
                            </header>

                            {chapters.length === 0 ? (
                                <p className='read-empty'>هذا الكتاب لا يحتوي على فصول بعد.</p>
                            ) : (
                                <>
                                    {activeChapter?.title && (
                                        <h2 className='read-chapter-title'>{activeChapter.title}</h2>
                                    )}
                                    <div
                                        className='read-content read-content--rich'
                                        dangerouslySetInnerHTML={{ __html: activeHtml }}
                                    />

                                    <div className='read-chapter-bookmark'>
                                        <button onClick={bookmarkChapter} disabled={bookmarking}>
                                            احفظ علامة عند هذا الفصل
                                        </button>
                                    </div>

                                    {chapters.length > 1 && (
                                        <nav className='read-chapter-nav' aria-label='التنقل بين الفصول'>
                                            <button
                                                disabled={activeIdx === 0}
                                                onClick={() => setActiveIdx(i => Math.max(0, i - 1))}
                                            >
                                                الفصل السابق
                                            </button>
                                            <span className='read-chapter-count'>
                                                {activeIdx + 1} / {chapters.length}
                                            </span>
                                            <button
                                                disabled={activeIdx === chapters.length - 1}
                                                onClick={() => setActiveIdx(i => Math.min(chapters.length - 1, i + 1))}
                                            >
                                                الفصل التالي
                                            </button>
                                        </nav>
                                    )}
                                </>
                            )}

                            <section className='read-rating-section'>
                                <h3>قيّم هذا الكتاب</h3>
                                <Stars
                                    interactive
                                    value={book.my_rating ?? 0}
                                    onSelect={rate}
                                />
                                {book.my_rating && (
                                    <div className='read-my-rating'>
                                        <small>تقييمك الحالي: {book.my_rating} / 5</small>
                                        <button
                                            type='button'
                                            className='read-rating-remove'
                                            onClick={removeRating}
                                        >
                                            احذف تقييمي
                                        </button>
                                    </div>
                                )}
                            </section>
                        </article>
                    </div>
                )}
            </div>
        </main>
        </>
    );
}
